import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import {
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { REQUIREMENTS_QUERY } from "queries/requirements";

import {
  RequirementsQuery,
  RequirementsQueryVariables,
  SubmissionType,
  RequirementStatus,
} from "generated-graphql/graphql";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { BulkActionsMenu } from "./BulkActionsMenu";

function prettyPrintSubmissionTypeEnum(submissionType: string) {
  return prettyPrintEnumValue(submissionType, ["EVQ"]);
}

function prettyPrintRequirementStatusEnum(status: string | null | undefined) {
  if (!status) {
    return "Active"; // Default to Active when null/undefined
  }
  return prettyPrintEnumValue(status);
}

export type Row = RequirementsQuery["requirements"]["items"][0];

export function Requirements() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();

  useBreadcrumb({
    label: "Requirements",
    to: "/staff/requirements",
  });

  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  const handleSelectedRowsChanged = useCallback((rows: Row[]) => {
    setSelectedRows(rows);
  }, []);

  const omnisearchPersistenceKey = "requirements-list-view-v3";
  const autoFlex = useAutoFlex(
    1.0,
    {
      tenant: 0.1,
      facility: 0.1,
      requirementJurisdictions: 0.1,
      submissionType: 0.1,
      requirementStatus: 0.1,
      frequency: 0.05,
      description: 0.3,
      actions: 0.15,
    },
    [
      "tenantName",
      "facilityName",
      "requirementJurisdictions",
      "submissionType",
      "requirementStatus",
      "frequency",
      "description",
      "actions",
    ]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "tenantName",
      headerName: "Organization",
      flex: autoFlex.get("tenantName"),
      minWidth: 50,
      sortable: true,
      renderCell: ({ row }) => {
        return <CellText variant="body2">{row.tenantName}</CellText>;
      },
    },
    {
      field: "facilityName",
      headerName: "Facility",
      flex: autoFlex.get("facilityName"),
      minWidth: 50,
      sortable: true,
      renderCell({ row }) {
        return <CellText variant="body2">{row.facilityName || "—"}</CellText>;
      },
    },
    {
      field: "jurisdictions",
      headerName: "Jurisdiction",
      flex: autoFlex.get("requirementJurisdictions"),
      minWidth: 50,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <CellText variant="body2">
            {row.requirementJurisdictions?.join(", ")}
          </CellText>
        );
      },
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      flex: autoFlex.get("submissionType"),
      minWidth: 50,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <CellText variant="body2">
            {prettyPrintSubmissionTypeEnum(row.submissionType) || "—"}
          </CellText>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: autoFlex.get("description"),
      minWidth: 50,
      sortable: true,
      renderCell: ({ row }) => {
        return <CellText variant="body2">{row.taskDescription}</CellText>;
      },
    },
    {
      field: "nextDueDate",
      headerName: "Next Due Date",
      flex: autoFlex.get("nextDueDate"),
      minWidth: 50,
      sortable: true,
      renderCell: ({ row }) => {
        return <CellText variant="body2">{row.nextDueDate}</CellText>;
      },
    },
    {
      field: "requirementStatus",
      headerName: "Opt In/Out Status",
      flex: autoFlex.get("requirementStatus"),
      minWidth: 50,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <CellText variant="body2">
            {prettyPrintRequirementStatusEnum(row.requirementStatus)}
          </CellText>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: autoFlex.get("actions"),
      minWidth: 50,
      sortable: false,
      renderCell: ({ row }) => {
        const submissionsUrl = `/o/${
          row.tenantId
        }/waste/submissions?omnisearch=${encodeURIComponent(
          `facilityId:${row.facilityId}`
        )}`;
        const isDeactivated =
          row.requirementStatus === RequirementStatus.Deactivated;

        return (
          <Button
            component={isDeactivated ? "button" : Link}
            to={isDeactivated ? undefined : submissionsUrl}
            target={isDeactivated ? undefined : "_blank"}
            rel={isDeactivated ? undefined : "noopener noreferrer"}
            variant="text"
            size="small"
            disabled={isDeactivated}
            endIcon={<OpenInNewIcon />}
            sx={{ textTransform: "none" }}
          >
            View In Submissions
          </Button>
        );
      },
    },
  ];

  const {
    debouncedOmnisearch,
    columns: omnisearchColumns,
    sortModel,
  } = useOmnisearchDatagrid({
    persistenceKey: omnisearchPersistenceKey,
    isURLDriven: true,
    columns,
    initialSortModel: [{ field: "tenantName", sort: "asc" }],
    initialPageSize: 50,
  });

  // Use the same query as the grid to get loading/data state
  const search = useMemo(
    () => debouncedOmnisearch.trim(),
    [debouncedOmnisearch]
  );

  const { data, loading } = useQuery<RequirementsQuery>(REQUIREMENTS_QUERY, {
    variables: {
      search,
      page: 0,
      pageSize: 1,
      sort: [...sortModel],
    },
    fetchPolicy: "cache-and-network",
  });

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Organization & Facility",
        // These field names must match the keys in additionalFilterColumns
        fields: ["tenant", "facility"],
      },
      {
        name: "Jurisdiction",
        fields: ["jurisdictions"],
      },
      {
        name: "Requirements",
        fields: ["submissionType", "requirementStatus"],
      },
    ],
    []
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        id="requirement-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            pb: 3,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h5"
                  color="text.primary"
                  pb={1}
                >
                  All Facilities Requirements
                </Typography>
                <Typography fontSize={12} component="p">
                  Regulatory requirements and deadlines. Use the search and
                  filters to find specific requirements.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <OmnisearchDataGrid<
            RequirementsQuery,
            RequirementsQueryVariables,
            Row
          >
            persistenceKey={omnisearchPersistenceKey}
            isURLDriven
            columns={omnisearchColumns}
            dataQuery={REQUIREMENTS_QUERY}
            getItems={(data) => data.requirements.items}
            getCount={(data) => data.requirements.count ?? 0}
            isRowSelectable={() => true}
            onSelectedRowsChanged={handleSelectedRowsChanged}
            disableRowSelectionOnClick
            commandButtons={[
              <Stack
                key="command-buttons"
                id="command-buttons"
                sx={{
                  marginBottom: "1rem",
                  marginTop: "1rem",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  gap: 2,
                }}
                alignItems="center"
              >
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  marginTop={0}
                  sx={{ flex: { sm: 1, lg: 2 }, alignItems: "center" }}
                >
                  <BulkActionsMenu selectedRows={selectedRows} />
                </Stack>

                <Box
                  id="test"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ExportDataButton
                    key="export"
                    exportType="requirements"
                    fileName="requirements"
                    filter={{
                      search: searchParams.get("omnisearch") || undefined,
                    }}
                    gridSettingsKey="requirements-list-view-v3"
                  />
                </Box>
              </Stack>,
            ]}
            initialPageSize={25}
            rowHeight={"auto"}
            checkboxSelection={true}
            columnVisibilityModel={{
              ...(isMobile && {
                facilityName: false,
                tags: false,
              }),
            }}
            additionalFilterColumns={[
              {
                header: "Organization",
                key: "tenant",
                filterKeyType: "multipleTenant",
              },
              {
                header: "Facility",
                key: "facility",
                filterKeyType: "multipleFacility",
              },
              {
                header: "Jurisdiction",
                key: "jurisdictions",
                filterKeyType: "text",
              },
              {
                header: "Submission Type",
                key: "submissionType",
                filterKeyType: "enum",
                enumValues: Object.values(SubmissionType),
                enumPresentationFunction: prettyPrintSubmissionTypeEnum,
              },
              {
                header: "Opt In/Out Status",
                key: "requirementStatus",
                filterKeyType: "enum",
                enumValues: Object.values(RequirementStatus),
                enumPresentationFunction: prettyPrintRequirementStatusEnum,
              },
            ]}
            excludeFilterColumns={[
              "tenantName",
              "facilityName",
              "jurisdictions",
              "submissionType",
              "requirementStatus",
              "facilityId",
              "title",
              "description",
              "actions",
              "nextDueDate",
            ]}
            filterGroups={filterGroups}
          />
        </Paper>
      </Stack>
    </Stack>
  );
}
